import { Component, forwardRef, OnInit, Input, OnChanges, Output, EventEmitter } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { JsonArrayComponentOption } from '../../utils';
import { cloneDeep } from 'lodash';
import { RappiderDataOrderService } from '../../services/data-order/data-order.service';
import { NotificationService } from '../../services/notification/notification.service';
import { ButtonComponentConfig, ButtonSize, ButtonType } from '../../utils/button';
import { IconType } from '../../utils/icon/icon-type.enum';
import { RADIO_GROUP_COMPONENT_CONFIG } from '../../utils/json-array/json-array-input-group.config';
import { JSON_ARRAY_LIST_GRID_CONFIG } from '../../utils/json-array/json-array-list-grid.config';
import { JSON_ARRAY_EMPTY_LIST_CONFIG } from '../../utils/json-array/json-array-empty-list.config';
import { CrudViewColumnType } from '../../utils/list-grid/crud-view-column-type.enum';
import { OrderChangeOutput } from '../../utils/list-grid/order-change-output.interface';

@Component({
  selector: 'rappider-json-array',
  templateUrl: './json-array.component.html',
  styleUrls: ['./json-array.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => RappiderJsonArrayComponent),
      multi: true
    }
  ]
})
export class RappiderJsonArrayComponent implements OnInit, OnChanges, ControlValueAccessor {

  @Input() previewFieldNames: string[];
  @Input() button: ButtonComponentConfig;
  @Input() modalTitle: string;

  @Output() blur = new EventEmitter<any>();

  /**
   * Json Array Value
   *
   * @type {any[]}
   * @memberof JsonArrayComponent
   */
  _value: any;
  localeValue: string;
  /* configs */
  JSON_ARRAY_LIST_GRID_CONFIG = cloneDeep(JSON_ARRAY_LIST_GRID_CONFIG);
  JSON_CODEMIRROR_SETTINGS = {
    language: 'json',
    automaticLayout: true,
    minimap: {
      enabled: false
    },
    formatOnPaste: true,
    formatOnType: true,
    validateOnType: false,
    validateOnPaste: false
  };

  monacoAdditionalButtons: ButtonComponentConfig[] = [
    {
      key: 'open-modal',
      icon: {
        name: 'fa-light fa-expand',
        type: IconType.FontAwesome,
        color: 'var(--text-color)'
      },
      size: ButtonSize.Small,
      type: ButtonType.Link
    }
  ];
  /* flags */
  isModalVisible = false;
  openCodemirror = false;
  monacoEditorInModalVisibility = false;
  isOpenModalMode = false;
  /* local values */
  currentIndex: number;
  currentValue: any;

  set modalVisible(value: boolean) {
    if (!value && this.isModalVisible) {
      // Modal is closing, immediately hide Monaco Editor
      this.monacoEditorInModalVisibility = false;
    }
    this.isModalVisible = value;
  }

  get modalVisible(): boolean {
    return this.isModalVisible;
  }

  componentOption = JsonArrayComponentOption.GridView;
  ComponentOption = JsonArrayComponentOption;
  radioGroupComponentConfig = RADIO_GROUP_COMPONENT_CONFIG;
  codeEditorVisible = false;
  JSON_ARRAY_EMPTY_LIST_CONFIG = JSON_ARRAY_EMPTY_LIST_CONFIG;

  get value() {
    return this._value ?? [];
  }

  set value(value: any) {
    this._value = value ?? [];
    this.onChange(value);
    this.onTouched();
    this.setListGridConfig();
    this.blur.emit(value);
  }

  constructor(
    private notificationService: NotificationService,
    private dataOrderService: RappiderDataOrderService
  ) { }

  ngOnInit(): void {
    this.initDefaults();
  }

  ngOnChanges(): void {
    this.initDefaults();
  }

  initDefaults() {
    if (!this.button) {
      this.button = {
        ...this.button,
        icon: {
          name: 'fas fa-plus',
        },
        text: 'Add New Item'
      };
    }
    if (!this.modalTitle) {
      this.modalTitle = 'SHARED.EDIT';
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  writeValue(value: any[]): void {
    if (value?.length > 0) {
      this._value = value;
    } else {
      this._value = [];
    }
    this.JSON_ARRAY_LIST_GRID_CONFIG = { ...this.JSON_ARRAY_LIST_GRID_CONFIG };
    this.setListGridConfig();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * on value change method
   *
   * @memberof JsonArrayComponent
   */
  onValueChange() {
    try {
      let currentStringValue;
      if (typeof this.currentValue === 'string') {
        currentStringValue = JSON.parse(this.currentValue);
      } else if (typeof this.currentValue === 'object') {
        currentStringValue = this.currentValue;
      } else {
        this.notificationService.createNotification(
          'error',
          'SHARED.ERROR',
          'JSON format is wrong. Please enter a valid JSON'
        );
        return;
      }

      // If we're in Open Modal mode, update the localeValue and close modal
      if (this.isOpenModalMode) {
        try {
          // Parse the currentValue to ensure it's valid JSON
          let parsedValue;
          if (typeof this.currentValue === 'string') {
            parsedValue = JSON.parse(this.currentValue);
          } else if (typeof this.currentValue === 'object') {
            parsedValue = this.currentValue;
          } else {
            this.notificationService.createNotification(
              'error',
              'SHARED.ERROR',
              'JSON format is wrong. Please enter a valid JSON'
            );
            return;
          }

          this.value = parsedValue;
          this.localeValue = this.currentValue;
          this.refreshValue();

          this.closeJsonArrayModal();

          this.notificationService.createNotification(
            'success',
            'SHARED.SUCCESSFUL',
            'Changes Saved Successfully'
          );
        } catch (error) {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'Failed to save changes'
          );
        }
        return;
      }

      /* checks if changed value is an edited value */
      if (this.currentIndex || this.currentIndex === 0) {
        /* find current object and change it to edited value*/
        try {
          this.value[this.currentIndex] = currentStringValue;
          this.closeJsonArrayModal();
          /* refresh value */
          this.refreshValue();

          this.notificationService.createNotification(
            'success',
            'SHARED.SUCCESSFUL',
            'Item Updated Successfully'
          );
        } catch (error) {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'JSON format is wrong. Please enter a valid JSON'
          );
        }

      } else {
        /* if the value is new (doesn't have a current index) push it to value. */
        try {
          /* parse json  */
          this.value.push(currentStringValue);
          this.closeJsonArrayModal();
          /* refresh value */
          this.refreshValue();

          this.notificationService.createNotification(
            'success',
            'SHARED.SUCCESSFUL',
            'New Item Added Successfully'
          );
        } catch (error) {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'JSON format is wrong. Please enter a valid JSON'
          );
        }
      }

    } catch (error) {
      this.notificationService.createNotification(
        'error',
        'SHARED.ERROR',
        'JSON format is wrong. Please enter a valid JSON'
      );
    }
  }

  /**
   * refresh value to trigger angular change detection
   *
   * @memberof JsonArrayComponent
   */
  refreshValue() {
    this.value = [...this.value];
  }

  /**
   * sets the list grid config
   *
   * @memberof JsonArrayComponent
   */
  setListGridConfig() {
    let columns = [];
    if (this.previewFieldNames?.length) {
      columns = this.previewFieldNames.map(fieldName => ({
        title: fieldName,
        fieldName: fieldName,
        type: CrudViewColumnType.Text
      }));
      this.JSON_ARRAY_LIST_GRID_CONFIG.columns = columns;
    } else {
      if (this.value?.length) {
        this.value?.forEach(item => {
          Object.keys(item)?.forEach(itemKey => {
            if (!columns.some(item => item.fieldName === itemKey)) {
              columns.push({
                title: itemKey,
                fieldName: itemKey,
                type: CrudViewColumnType.Text
              });
            }
          });
        });
        /* to convert 2dimensional array to 1 dimensional array */
        columns = [].concat(...columns);
        this.JSON_ARRAY_LIST_GRID_CONFIG.columns = columns;
      }
    }

  }

  /**
   * Fires when item action clicked
   *
   * @memberof JsonArrayComponent
   */
  itemActionClick(event: any) {
    /* gets the index of item */
    const index = event.rowIndex;
    /* checks the action type */
    if (event.action.name === 'EDIT') {
      /* stringify value then assign to current value */
      this.currentValue = JSON.stringify(this.value[index]);
      /* sets the current index */
      this.currentIndex = index;
      /* opens modal */
      this.isOpenModalMode = false;
      this.modalVisible = true;
      /*
       * sets true after 500ms
       * used to render the codemirror component properly as codemirror gutter doesn't initialized as it should be.
       */
      setTimeout(() => this.monacoEditorInModalVisibility = true, 500);
    } else if (event.action.name === 'DELETE') {
      /* deletes the value */
      this.value = this.value.filter(item => item !== this.value[index]);
    }
  }

  /**
   * fires when add new button clicked
   *
   * @memberof JsonArrayComponent
   */
  addNewJson() {
    /* resets current value */
    this.currentValue = '';
    /* resets current index */
    this.currentIndex = null;
    /* opens modal */
    this.isOpenModalMode = false;
    this.modalVisible = true;
    setTimeout(() => {
      this.monacoEditorInModalVisibility = true;
    }, 400);
  }

  /**
   * list grid order change
   *
   * @param {OrderChangeOutput} output
   * @memberof JsonArrayComponent
   */
  onOrderChange(output: OrderChangeOutput) {
    const orderedValue = this.dataOrderService.changeOrderByStartAndEndIndex(
      this.value,
      output.data,
      output.orderStartIndex,
      output.orderEndIndex
    );
    this.value = orderedValue;
  }

  closeJsonArrayModal() {
    // Immediately hide Monaco Editor to prevent spin loading
    this.monacoEditorInModalVisibility = false;

    // Reset other flags
    this.currentValue = null;
    this.modalVisible = false;
    // Don't reset openCodemirror as it affects the main page
    this.isOpenModalMode = false;
    this.codeEditorVisible = false;
    this.JSON_ARRAY_LIST_GRID_CONFIG = { ...this.JSON_ARRAY_LIST_GRID_CONFIG };
  }

  onComponentOptionChange(option: JsonArrayComponentOption) {
    if (option === JsonArrayComponentOption.CodeMirror) {
      this.localeValue = JSON.stringify(this.value, null, 2);
      // Immediately set openCodemirror to true to prevent spin loading
      this.openCodemirror = true;
    }
    if (option === JsonArrayComponentOption.GridView) {
      if (this.value && this.value.length > 0) {
        try {
          this.value = JSON.parse(this.value);
        } catch {
          this.value = this.value || [];
        }
      }
    }
    this.componentOption = option;
  }

  onCodemirrorValueChange(value) {
    this.localeValue = value;
  }

  onCodemirrorSave() {
    try {
      if (typeof this.localeValue === 'string') {
        this.value = JSON.parse(this.localeValue);
      } else if (typeof this.localeValue === 'object') {
        this.value = this.localeValue;
      } else {
        this.notificationService.createNotification(
          'error',
          'SHARED.ERROR',
          'JSON format is wrong. Please enter a valid JSON'
        );
        return;
      }

      this.notificationService.createNotification(
        'success',
        'SHARED.SUCCESSFUL',
        'Json Saved Successfully'
      );
    } catch (error) {
      this.notificationService.createNotification(
        'error',
        'SHARED.ERROR',
        'JSON format is wrong. Please enter a valid JSON'
      );
    }
  }

  onAdditionalButtonClick(button: ButtonComponentConfig) {
    if (button.key === 'open-modal') {
      if (this.componentOption === JsonArrayComponentOption.CodeMirror) {
        this.currentValue = this.localeValue;
      } else {
        this.currentValue = JSON.stringify(this.value, null, 2);
      }

      // Open Modal mode - only for viewing/editing, not for adding new items
      this.isOpenModalMode = true;
      this.modalVisible = true;
      setTimeout(() => {
        this.monacoEditorInModalVisibility = true;
      }, 400);
    }
  }

}
